import { faArrowsRotate, faBan, faSave, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faSync } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Dispatch, SetStateAction, useCallback } from 'react';
import VoxelDialog from '../../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionButton from '../../../../../components/voxel/Dialogs/VoxelDialogActionButton';
import VoxelDialogActionCancel from '../../../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../../components/voxel/Dialogs/VoxelDialogContent';
import { DomainTypeGq } from '../../../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import MetaInput from './MetaInput';
import MetaStatistics from './MetaStatistics';
import { UrlInput } from './UrlInput';
import { useMetaRuleForm } from './formLogic/useMetaRuleForm';
import { useUrlInput } from './useUrlInput';

export interface MetaRuleDialogProps {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  metaRuleId?: number;
  setMetaRuleId: Dispatch<SetStateAction<number>>;
  reload: () => Promise<any>;
  project: ProjectGq;
}

const MetaRuleDialog: React.FC<MetaRuleDialogProps> = ({
  openDialog,
  setOpenDialog,
  project,
  metaRuleId,
  setMetaRuleId,
  reload,
}) => {
  // Close dialog
  const closeDialog = useCallback(() => {
    setMetaRuleId(null);
    formBag.resetForm();
    setOpenDialog(false);
  }, []);

  // Form values
  const formBag = useMetaRuleForm(project, metaRuleId, setMetaRuleId, reload);

  // Delete logic
  const deleteMutation = projectsApiService.mutations.contentPlan.useDeleteMetaRule();
  const handleDelete = (metaRuleId: number) => {
    deleteMutation({
      projectId: project.id,
      metaRuleId: metaRuleId,
    })
      .catch((reason: any) => console.error(reason))
      .then(reload)
      .finally(() => closeDialog());
  };

  const urlInput = useUrlInput({
    project,
    clientUrl: project.domains.find((d) => d.type === DomainTypeGq.PROJECT).hostName + '/',
    formBag: formBag,
  });

  const submitTitle = useCallback(() => {
    if (metaRuleId === null) return 'Save draft';
    else if (metaRuleId !== null && formBag.dirty) return 'Save changes';
    else if (metaRuleId !== null && formBag.values.active) return 'Turn off Sync';
    else if (metaRuleId !== null && !formBag.values.active) return 'Turn on Sync';
    else return 'Error';
  }, [metaRuleId, formBag.dirty, formBag.values.active]);

  const submitIcon = useCallback(() => {
    if (metaRuleId === null) return <FontAwesomeIcon icon={faSave} />;
    else if (metaRuleId !== null && formBag.dirty) return <FontAwesomeIcon icon={faSave} />;
    else if (metaRuleId !== null && formBag.values.active) return <FontAwesomeIcon icon={faBan} />;
    else if (metaRuleId !== null && !formBag.values.active)
      return <FontAwesomeIcon icon={faArrowsRotate} />;
    else return null;
  }, [metaRuleId, formBag.dirty, formBag.values.active, formBag.isSubmitting]);

  const submitDisabled = useCallback(() => {
    if (formBag.isSubmitting) return true;
    else if (metaRuleId === null && !formBag.dirty) return true;
    else return false;
  }, [submitTitle, formBag.isSubmitting]);

  const titleIcon = useCallback(() => {
    if (formBag.values.id !== null && formBag.values.active) {
      return <FontAwesomeIcon icon={faSync} className={'text-success-500'} size="xl" />;
    } else if (formBag.values.id !== null && !formBag.values.active) {
      return <FontAwesomeIcon icon={faBan} className={'text-warning-600'} size="xl" />;
    } else return null;
  }, [metaRuleId, formBag.dirty, formBag.values.active, formBag.isSubmitting]);

  return (
    <VoxelDialog
      maxWClass={'max-w-4xl'}
      open={openDialog}
      onClose={() => closeDialog()}
      title={formBag.values.id === null ? 'Create Meta rule' : 'Update Meta rule'}
      titleIcon={titleIcon()}
      subtitle={'Replace titles, H1s and meta descriptions, using primary and secondary keyword.'}
      closeOnBackdrop
    >
      <VoxelDialogContent>
        <div className="flex flex-col w-full gap-2">
          <UrlInput
            placeholder={!formBag.values.urlMatchType ? '/...' : ''}
            updating={formBag.isSubmitting}
            urlMatchType={formBag.values.urlMatchType}
            setUrlMatchType={(value: string) => formBag.setFieldValue('urlMatchType', value)}
            urlInputBag={urlInput}
            url={formBag.values.url}
            setUrl={(value: string) => formBag.setFieldValue('url', value)}
          />
          <MetaInput
            type={'input'}
            label={'Title'}
            placeholder={`Women's [primary keyword]`}
            valueKey={'title'}
            value={formBag.values.title}
            setValue={(value: string) => formBag.setFieldValue('title', value)}
            disabled={formBag.isSubmitting}
          />
          <MetaInput
            type={'input'}
            label={'H1'}
            placeholder={'[Primary keyword]'}
            valueKey={'h1'}
            value={formBag.values.h1}
            setValue={(value: string) => formBag.setFieldValue('h1', value)}
            disabled={formBag.isSubmitting}
          />
          <MetaInput
            type={'textarea'}
            label={'Meta description'}
            placeholder={
              'Shop a wide selection of [primary keyword] from your favorite designer brands, online or in store. Find the perfect [secondary keyword] for any occasion. ✓ Free shipping and free returns.'
            }
            valueKey={'meta'}
            value={formBag.values.meta}
            setValue={(value: string) => formBag.setFieldValue('meta', value)}
            disabled={formBag.isSubmitting}
          />
          <div className="flex flex-row flex-nowrap">
            <MetaStatistics
              label={'Statistics with above generation'}
              urlList={urlInput.relevantCurrentUrls}
              values={formBag.values}
              hint={false}
            />
            {!!formBag.values.id && formBag.dirty && (
              <MetaStatistics
                label={'Original statistics'}
                urlList={urlInput.relevantInitialUrls}
                values={formBag.initialValues}
                hint={!formBag.dirty}
              />
            )}
          </div>
          <p className="mt-6 text-xs">
            *Titles should be somewhere between 30-60 characters and Metas descriptions between
            100-160 characters.
          </p>
        </div>
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel onClick={() => closeDialog()} />
        {metaRuleId !== null && (
          <VoxelDialogActionButton
            title={'Delete'}
            onClick={() => handleDelete(metaRuleId)}
            disabled={formBag.isSubmitting}
            endIcon={<FontAwesomeIcon icon={faTrash} />}
          />
        )}
        <VoxelDialogActionSubmit
          endIcon={submitIcon()}
          title={submitTitle()}
          onClick={() => {
            if (metaRuleId === null || formBag.dirty) formBag.submitForm();
            else
              formBag.setFieldValue('active', !formBag.values.active)?.then(() => {
                formBag.submitForm();
              });
          }}
          disabled={submitDisabled()}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default MetaRuleDialog;
